// The development performance meter (PRD section 19.17, "Medidor de rendimiento"): figures to check the
// H0 budget on the iPad, plus the levers for the decision gate (stress, animations, shadows, resolution).
import type { Resolution } from '../render/stage';
import { segmentedPill, toggleSwitch } from './controls';
import { formatNumber, h, icon } from './dom';
import { ICONS } from './icons';

export interface PerfReadout {
  readonly fps: number;
  readonly workMs: number;
  readonly worstMs: number;
  readonly calls: number;
  readonly triangles: number;
  /** JavaScript heap in MB, where the browser reports it (not in Safari). */
  readonly heapMb: number | undefined;
  readonly gpuMb: number;
  readonly frames: number;
  readonly mode: 'idle' | 'ambient' | 'interactive';
  readonly objects: number;
  readonly pixels: string;
  readonly readyMs: number | undefined;
}

export interface PerfLevers {
  readonly stress: boolean;
  readonly animations: boolean;
  readonly shadows: boolean;
  readonly resolution: Resolution;
}

export interface PerfPanelHandlers {
  readonly onStress: (on: boolean) => void;
  readonly onAnimations: (on: boolean) => void;
  readonly onShadows: (on: boolean) => void;
  readonly onResolution: (value: Resolution) => void;
  readonly onClose: () => void;
}

const MODE_TEXT: Record<PerfReadout['mode'], string> = {
  idle: 'Quieta',
  ambient: 'Solo animaciones',
  interactive: 'Tocando o moviendo',
};

/** Budget of report section 4.3, to colour figures that go over it (amber, never red). */
const BUDGET = { calls: 150, heapMb: 300, readyMs: 3000 } as const;

export class PerfPanel {
  readonly el: HTMLElement;
  private readonly values = new Map<string, HTMLElement>();

  constructor(levers: PerfLevers, handlers: PerfPanelHandlers) {
    const close = h('button', { type: 'button', class: 'icon-button', 'aria-label': 'Cerrar el medidor' }, icon(ICONS.close));
    close.addEventListener('click', handlers.onClose);
    const header = h('header', { class: 'panel__header' }, icon(ICONS.gauge, 'icon panel__icon'), h('div', { class: 'panel__title' }, 'Rendimiento', h('small', {}, 'Solo desarrollo')), close);

    const stats = h('dl', { class: 'stats' });
    const row = (key: string, label: string): void => {
      const value = h('dd', { class: 'stats__value', 'data-stat': key }, '–');
      this.values.set(key, value);
      stats.append(h('div', { class: 'stats__row' }, h('dt', {}, label), value));
    };
    row('fps', 'Fotogramas por segundo');
    row('ms', 'ms por fotograma');
    row('worst', 'Peor intervalo');
    row('calls', 'Llamadas de dibujo');
    row('triangles', 'Triángulos');
    row('heap', 'Memoria JS');
    row('gpu', 'Memoria GPU (aprox.)');
    row('objects', 'Objetos animados y decorados');
    row('frames', 'Fotogramas dibujados');
    row('mode', 'Estado');
    row('pixels', 'Resolución');
    row('ready', 'Lista para tocar en');

    const stress = toggleSwitch({ label: 'Modo de estrés', checked: levers.stress, onChange: handlers.onStress });
    const animations = toggleSwitch({ label: 'Animaciones', checked: levers.animations, onChange: handlers.onAnimations });
    const shadows = toggleSwitch({ label: 'Sombras', checked: levers.shadows, onChange: handlers.onShadows });
    const resolution = segmentedPill<Resolution>({
      label: 'Resolución',
      tone: 'white',
      size: 'sm',
      value: levers.resolution,
      options: [
        { id: 'baja', label: '1×' },
        { id: 'media', label: '1,5×' },
        { id: 'alta', label: '2×' },
      ],
      onChange: handlers.onResolution,
    });
    const lever = (label: string, sub: string | undefined, control: HTMLElement): HTMLElement =>
      h('div', { class: 'lever' }, h('div', { class: 'lever__text' }, label, ...(sub ? [h('small', {}, sub)] : [])), control);

    this.el = h(
      'section',
      { class: 'panel perf', 'aria-label': 'Medidor de rendimiento' },
      header,
      h(
        'div',
        { class: 'panel__body' },
        stats,
        h(
          'div',
          { class: 'levers' },
          lever('Modo de estrés', 'Cinco veces más objetos', stress.el),
          lever('Animaciones', 'Vehículos en movimiento', animations.el),
          lever('Sombras', undefined, shadows.el),
          lever('Resolución', undefined, resolution.el),
        ),
      ),
    );
  }

  update(r: PerfReadout): void {
    this.set('fps', formatNumber(r.fps));
    this.set('ms', formatNumber(r.workMs, 1));
    this.set('worst', r.worstMs > 0 ? `${formatNumber(r.worstMs)} ms` : '–');
    this.set('calls', formatNumber(r.calls), r.calls >= BUDGET.calls);
    this.set('triangles', formatNumber(r.triangles));
    this.set('heap', r.heapMb === undefined ? 'No disponible' : `${formatNumber(r.heapMb)} MB`, r.heapMb !== undefined && r.heapMb >= BUDGET.heapMb);
    this.set('gpu', `${formatNumber(r.gpuMb)} MB`);
    this.set('objects', formatNumber(r.objects));
    this.set('frames', formatNumber(r.frames));
    this.set('mode', MODE_TEXT[r.mode]);
    this.set('pixels', r.pixels);
    this.set('ready', r.readyMs === undefined ? '–' : `${formatNumber(r.readyMs / 1000, 2)} s`, r.readyMs !== undefined && r.readyMs >= BUDGET.readyMs);
  }

  private set(key: string, text: string, over = false): void {
    const el = this.values.get(key);
    if (!el) return;
    if (el.textContent !== text) el.textContent = text;
    el.classList.toggle('stats__value--over', over);
  }
}
